/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useRef } from "react";

import { ctaDestination, trackEvent } from "@/client/analytics/track";
import { TheResumeLogo } from "@/client/components/theResumeLogo";
import { apex25HomeUrl, firstRoundPublicUrl } from "@/client/utils/hubUrls";

import { entry, type Side } from "./content";
import { Word } from "./word";

type Props = {
  lastSide?: Side;
  onChoose: (side: Side) => void;
};

const scrollToFork = () => {
  const target = document.getElementById("hub-hero");
  if (!target) return;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
};

/**
 * The first fold: the logo, one question and two doors. As the page scrolls, the doors part and
 * the fold dissolves while the hub below rises into view.
 */
export const Entry = ({ lastSide, onChoose }: Props) => {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const lift = useTransform(scrollYProgress, [0, 1], [0, -56]);
  const leftX = useTransform(scrollYProgress, [0, 1], [0, -84]);
  const rightX = useTransform(scrollYProgress, [0, 1], [0, 84]);
  const still = Boolean(reduced);

  const doors = [
    {
      side: "candidate" as const,
      data: entry.candidate,
      href: firstRoundPublicUrl(),
      x: leftX,
    },
    {
      side: "recruiter" as const,
      data: entry.recruiter,
      href: apex25HomeUrl(),
      x: rightX,
    },
  ];

  return (
    <section ref={ref} className="hub-entry" aria-labelledby="hub-title">
      <span className="hub-entry__glow hub-entry__glow--candidate" aria-hidden="true" />
      <span className="hub-entry__glow hub-entry__glow--recruiter" aria-hidden="true" />

      <motion.div
        className="hub-entry__stage"
        style={still ? undefined : { opacity: fade, y: lift }}
      >
        <div className="hub-entry__logo">
          <TheResumeLogo size={260} />
        </div>

        <h1 id="hub-title" className="hub-entry__title">
          {entry.title.split(" ").map((word, index, words) => (
            <span key={word}>
              <Word index={index}>{word}</Word>
              {index < words.length - 1 ? " " : ""}
            </span>
          ))}
        </h1>

        <div className="hub-entry__doors">
          {doors.map(({ side, data, href, x }) => (
            <motion.div key={side} className="hub-entry__slot" style={still ? undefined : { x }}>
              <a
                className={`hub-entry__door hub-entry__door--${side}`}
                href={href}
                onClick={() => {
                  onChoose(side);
                  trackEvent("cta_clicked", {
                    cta_id: `hub_${side}_entry`,
                    cta_location: "hub_entry",
                    destination: ctaDestination(href),
                  });
                }}
              >
                <span className="hub-entry__label">
                  {data.label}
                  {lastSide === side && <em>Last time</em>}
                </span>
                <span className="hub-entry__name">{data.name}</span>
                <span className="hub-entry__line">{data.line}</span>
                <span className="hub-entry__go" aria-hidden="true">
                  <ArrowUpRight size={22} />
                </span>
              </a>
            </motion.div>
          ))}
          <span className="hub-entry__or" aria-hidden="true">
            {entry.or}
          </span>
        </div>
      </motion.div>

      <button type="button" className="hub-entry__cue" onClick={scrollToFork}>
        {entry.cue}
        <ChevronDown size={16} aria-hidden="true" />
      </button>
    </section>
  );
};
